import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, installments CTA close-on-click, and cart countdown text.
 *
 * @extends {Component}
 */

class CartDeliveryMessage extends HTMLElement {
  connectedCallback() {
    this.updateDeliveryMessage();

    this.deliveryInterval = setInterval(() => {
      this.updateDeliveryMessage();
    }, 60000);
  }

  disconnectedCallback() {
    clearInterval(this.deliveryInterval);
  }

  // Match the product page configuration
  shippingTimeZone = 'America/New_York';
  cutoffHour = 12;

  getOriginDateParts(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.shippingTimeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(date);

    return Object.fromEntries(
      parts.map(part => [part.type, Number(part.value)])
    );
  }

  getObservedHoliday(date) {
    const day = date.getUTCDay();

    if (day === 6) {
      date.setUTCDate(date.getUTCDate() - 1);
    } else if (day === 0) {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    return date;
  }

  nthWeekday(year, month, weekday, nth) {
    const date = new Date(Date.UTC(year, month, 1));
    const offset = (weekday - date.getUTCDay() + 7) % 7;

    date.setUTCDate(1 + offset + (nth - 1) * 7);

    return date;
  }

  lastWeekday(year, month, weekday) {
    const date = new Date(Date.UTC(year, month + 1, 0));
    const offset = (date.getUTCDay() - weekday + 7) % 7;

    date.setUTCDate(date.getUTCDate() - offset);

    return date;
  }

  getHolidayDates(year) {
    const holidays = new Set();

    const add = (date) => {
      holidays.add(date.toISOString().slice(0, 10));
    };

    // Observed New Year's Day
    for (const y of [year - 1, year, year + 1]) {
      add(this.getObservedHoliday(
        new Date(Date.UTC(y, 0, 1))
      ));
    }

    // Martin Luther King Jr. Day
    add(this.nthWeekday(year, 0, 1, 3));

    // Washington's Birthday
    add(this.nthWeekday(year, 1, 1, 3));

    // Memorial Day
    add(this.lastWeekday(year, 4, 1));

    // Juneteenth
    add(this.getObservedHoliday(
      new Date(Date.UTC(year, 5, 19))
    ));

    // Independence Day
    add(this.getObservedHoliday(
      new Date(Date.UTC(year, 6, 4))
    ));

    // Labor Day
    add(this.nthWeekday(year, 8, 1, 1));

    // Columbus Day
    add(this.nthWeekday(year, 9, 1, 2));

    // Veterans Day
    add(this.getObservedHoliday(
      new Date(Date.UTC(year, 10, 11))
    ));

    // Thanksgiving
    add(this.nthWeekday(year, 10, 4, 4));

    // Christmas
    add(this.getObservedHoliday(
      new Date(Date.UTC(year, 11, 25))
    ));

    return holidays;
  }

  isBusinessDay(date) {
    const day = date.getUTCDay();

    // Skip weekends
    if (day === 0 || day === 6) {
      return false;
    }

    const holidays = this.getHolidayDates(
      date.getUTCFullYear()
    );

    return !holidays.has(
      date.toISOString().slice(0, 10)
    );
  }

  addBusinessDays(date, days) {
    const result = new Date(date);
    let addedDays = 0;

    while (addedDays < days) {
      result.setUTCDate(result.getUTCDate() + 1);

      if (this.isBusinessDay(result)) {
        addedDays++;
      }
    }

    return result;
  }

  getShippingDate() {
    const now = new Date();
    const origin = this.getOriginDateParts(now);

    const date = new Date(Date.UTC(
      origin.year,
      origin.month - 1,
      origin.day
    ));

    // Orders after 12 PM Eastern or on a
    // non-business day move to next business day.
    if (
      origin.hour >= this.cutoffHour ||
      !this.isBusinessDay(date)
    ) {
      do {
        date.setUTCDate(date.getUTCDate() + 1);
      } while (!this.isBusinessDay(date));
    }

    return date;
  }

  updateDeliveryMessage() {
    const isHeavy = this.dataset.heavy === 'true';

    const deliveryText = this.querySelector(
      '[data-delivery-text]'
    );

    if (!deliveryText) return;

    /*
     * Fast/normal cart:
     * 3-4 business days
     *
     * Slow/heavy cart:
     * 5-6 business days
     */

    const startDays = isHeavy ? 6 : 3;
    const endDays = isHeavy ? 7 : 4;

    const shippingDate = this.getShippingDate();

    const deliveryStart = this.addBusinessDays(
      shippingDate,
      startDays
    );

    const deliveryEnd = this.addBusinessDays(
      shippingDate,
      endDays
    );

    const options = {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    };

    const startFormatted =
      deliveryStart.toLocaleDateString(
        'en-US',
        options
      );

    const endFormatted =
      deliveryEnd.toLocaleDateString(
        'en-US',
        options
      );

    deliveryText.innerHTML =
      `Delivery <strong>${startFormatted} - ${endFormatted}</strong>`;
  }
}

if (!customElements.get('cart-delivery-message')) {
  customElements.define(
    'cart-delivery-message',
    CartDeliveryMessage
  );
}




class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setInterval> | null} */
  #countdownInterval = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    // The restore path sets [open] before this module loads,
    // so the theme-drawer:open event may have already fired.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }
  }

  /**
   * Handles the theme-drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Update delivery date message.
    this.#updateDeliveryMessages();

    // Start/update the persistent cart countdown.
    this.#updateCartDisputeText();

    // Close cart drawer when installments CTA is clicked
    // to avoid overlapping dialogs.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog>
    // (e.g. quick-add), defer auto-open until that dialog closes.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();

            // Cart HTML may have been re-rendered,
            // so update the delivery message again.
            this.#updateDeliveryMessages();

            // Cart HTML may have been re-rendered,
            // so reconnect the countdown to the new element.
            if (this.#themeDrawer?.isOpen) {
              this.#updateCartDisputeText();
            }
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }

  /**
   * Refresh all delivery message elements
   * inside the cart drawer.
   */
  #updateDeliveryMessages() {
    this.querySelectorAll('cart-delivery-message')
      .forEach((element) => {
        if (
          typeof element.updateDeliveryMessage === 'function'
        ) {
          element.updateDeliveryMessage();
        }
      });
  }

  /**
   * Updates cart dispute text with a persistent 20-minute countdown.
   *
   * Countdown end time is saved in a cookie so refreshing
   * the page does not restart the countdown.
   */
  #updateCartDisputeText = () => {
    const dialog = this.#dialog;
    if (!dialog) return;

    const disputeText = dialog.querySelector('.cartdisputetext');
    const countEl = dialog.querySelector('#cart-countnumberas');

    if (!disputeText || !countEl) return;

    const COOKIE_NAME = 'cart_countdown_end';
    const COUNTDOWN_SECONDS = 20 * 60;
    const COUNTDOWN_MS = COUNTDOWN_SECONDS * 1000;

    /**
     * Get a cookie value.
     */
    const getCookie = (name) => {
      const cookies = document.cookie.split(';');

      for (const cookie of cookies) {
        const [key, ...valueParts] = cookie.trim().split('=');

        if (key === name) {
          return decodeURIComponent(valueParts.join('='));
        }
      }

      return null;
    };

    /**
     * Get existing countdown end timestamp.
     */
    let endTime = Number(getCookie(COOKIE_NAME));

    /**
     * If no valid timer exists, start a new 20-minute countdown.
     */
    if (
      !endTime ||
      Number.isNaN(endTime) ||
      endTime <= Date.now()
    ) {
      endTime = Date.now() + COUNTDOWN_MS;

      document.cookie =
        `${COOKIE_NAME}=${encodeURIComponent(endTime)}; ` +
        `Max-Age=${COUNTDOWN_SECONDS}; ` +
        `Path=/; ` +
        `SameSite=Lax`;
    }

    /**
     * Render current countdown.
     */
    const renderCountdown = () => {
      const remaining = Math.max(
        0,
        endTime - Date.now()
      );

      const totalSeconds = Math.ceil(
        remaining / 1000
      );

      const minutes = Math.floor(
        totalSeconds / 60
      );

      const seconds =
        totalSeconds % 60;

      countEl.textContent =
        `${String(minutes).padStart(2, '0')}:` +
        `${String(seconds).padStart(2, '0')}`;

      disputeText.style.display = 'flex';

      /**
       * Stop timer at 00:00.
       */
      if (remaining <= 0) {
        countEl.textContent = '00:00';

        if (this.#countdownInterval) {
          clearInterval(this.#countdownInterval);
          this.#countdownInterval = null;
        }
      }
    };

    /**
     * Kill any previous interval before starting another.
     */
    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }

    /**
     * Render immediately so there isn't a 1-second delay.
     */
    renderCountdown();

    /**
     * Update every second.
     */
    this.#countdownInterval = setInterval(
      renderCountdown,
      1000
    );
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}